import React from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Paper,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Vehicle, VehicleDocument, MileageReport } from '../../types/vehicle';
import { FuelConsumption } from '../../types/fuelConsumption';
import { formatDate } from '../../utils/dateUtils';
import MaintenanceList from '../maintenance/MaintenanceList';
import { MaintenancePlanList } from '../maintenance/MaintenancePlanList';
import TabPanel from '../common/TabPanel';
import TechnicalInspectionPanel from './TechnicalInspectionPanel';
import { useCurrency } from '../../hooks/useCurrency';

interface VehicleTabContentProps {
  vehicle: Vehicle;
  activeTab: number;
  mileageReports: MileageReport[];
  fuelConsumptions: FuelConsumption[];
  reportUsers: Record<string, { displayName: string }>;
  isLoadingFuel: boolean;
  isMobile: boolean;
  isAdmin: boolean;
  loading: boolean;
  currentUserId?: string;
  onShowMaintenanceForm: () => void;
  onShowMaintenancePlanForm: () => void;
  onShowMileageDialog: () => void;
  onShowFuelEntryDialog: () => void;
  onShowUploadDialog: () => void;
  onUpdate: () => void;
  onEditMaintenance: (record: any) => void;
  onEditFuel: (record: FuelConsumption) => void;
  onDeleteFuel: (record: FuelConsumption) => void;
  onImageClick: (url: string) => void;
  onDeleteDocument: (url: string) => void;
}

const VehicleTabContent: React.FC<VehicleTabContentProps> = ({
  vehicle,
  activeTab,
  mileageReports,
  fuelConsumptions,
  reportUsers,
  isLoadingFuel,
  isMobile,
  isAdmin,
  loading,
  currentUserId,
  onShowMaintenanceForm,
  onShowMaintenancePlanForm,
  onShowMileageDialog,
  onShowFuelEntryDialog,
  onShowUploadDialog,
  onUpdate,
  onEditMaintenance,
  onEditFuel,
  onDeleteFuel,
  onImageClick,
  onDeleteDocument
}) => {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency(vehicle.organizationId);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Maintenance Tab */}
      <TabPanel value={activeTab} index={0}>
        <MaintenanceList
          organizationId={vehicle.organizationId}
          filters={{ vehicleId: vehicle.id }}
          onEdit={onEditMaintenance}
        />
        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button
            variant="contained"
            onClick={onShowMaintenanceForm}
            fullWidth={isMobile}
          >
            {t('maintenance.addNew')}
          </Button>
        </Box>
      </TabPanel>

      {/* Plans de maintenance Tab */}
      <TabPanel value={activeTab} index={1}>
        <MaintenancePlanList
          vehicleId={vehicle.id}
          organizationId={vehicle.organizationId}
        />
      </TabPanel>

      {/* Fuel Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <Button
            variant="outlined"
            onClick={onUpdate}
            disabled={isLoadingFuel}
            fullWidth={isMobile}
            startIcon={<RefreshIcon />}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            onClick={onShowFuelEntryDialog}
            fullWidth={isMobile}
          >
            {t('fuelReceipt.quickEntry.title')}
          </Button>
        </Box>
        {isLoadingFuel ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : fuelConsumptions.length > 0 ? (
          <List>
            {fuelConsumptions.map((record) => {
              // Vérifier si l'utilisateur peut éditer/supprimer ce ticket
              const canModify = isAdmin || record.createdBy === currentUserId;

              return (
                <ListItem
                  key={record.id}
                  sx={{
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {record.receiptUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => record.receiptUrl && onImageClick(record.receiptUrl)}
                          startIcon={<ImageIcon />}
                        >
                          {t('vehicle.details.buttons.viewFuelReceipt')}
                        </Button>
                      )}
                      {canModify && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => onEditFuel(record)}
                            color="primary"
                            title={t('common.edit')}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteFuel(record)}
                            color="error"
                            title={t('common.delete')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`${record.volume.toFixed(2)} L - ${formatCurrency(record.amount)}`}
                    secondary={
                      <Box>
                        <Box>{record.supplier}</Box>
                        <Box>{formatDate(record.date)}</Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography color="textSecondary">{t('vehicle.details.messages.noFuelRecords')}</Typography>
        )}
      </TabPanel>

      {/* Technical Inspection Tab */}
      <TabPanel value={activeTab} index={3}>
        <TechnicalInspectionPanel
          vehicle={vehicle}
          onUploadDocument={onShowUploadDialog}
        />
      </TabPanel>

      {/* Alerts Tab */}
      <TabPanel value={activeTab} index={4}>
        <Typography color="textSecondary">{t('vehicle.details.messages.noAlerts')}</Typography>
      </TabPanel>

      {/* Mileage Reports Tab */}
      <TabPanel value={activeTab} index={5}>
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <Button
            variant="outlined"
            onClick={onUpdate}
            startIcon={<RefreshIcon />}
            fullWidth={isMobile}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            onClick={onShowMileageDialog}
            fullWidth={isMobile}
          >
            {t('vehicle.details.buttons.enterNewMileage')}
          </Button>
        </Box>
        <List>
          {mileageReports.map((report) => (
            <ListItem key={report.id}>
              <ListItemText
                primary={`${report.mileage} km - ${formatDate(report.date.toDate())}`}
                secondary={
                  <Box mt={1}>
                    {reportUsers?.[report.reportedBy] && (
                      <Box color="text.secondary">
                        {t('vehicle.details.messages.reportedBy')}: {reportUsers?.[report.reportedBy]?.displayName}
                      </Box>
                    )}
                    {report.notes && (
                      <Box mt={1}>
                        {report.notes}
                      </Box>
                    )}
                    {report.imageUrl && (
                      <Box mt={1}>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: 'center' }}>
                          <Paper
                            elevation={0}
                            sx={{
                              width: 60,
                              height: 60,
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <img
                              src={report.imageUrl}
                              alt={t('vehicle.details.labels.dashboardPhoto')}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer'
                              }}
                              onClick={() => report.imageUrl && onImageClick(report.imageUrl)}
                            />
                          </Paper>
                          <Button
                            startIcon={<ImageIcon />}
                            size="small"
                            onClick={() => report.imageUrl && onImageClick(report.imageUrl)}
                            fullWidth={isMobile}
                            sx={{ mt: isMobile ? 1 : 0 }}
                          >
                            {t('vehicle.details.buttons.viewDashboardPhoto')}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
          {mileageReports.length === 0 && (
            <Typography color="textSecondary">{t('vehicle.details.messages.noMileageReports')}</Typography>
          )}
        </List>
      </TabPanel>

      {/* Documents Tab */}
      <TabPanel value={activeTab} index={6}>
        <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
          <Button
            variant="outlined"
            onClick={onUpdate}
            startIcon={<RefreshIcon />}
            fullWidth={isMobile}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={onShowUploadDialog}
            fullWidth={isMobile}
          >
            {t('vehicle.details.buttons.uploadDocument')}
          </Button>
        </Box>

        <List sx={{ 
          '& .MuiListItem-root': { 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 1,
            padding: isMobile ? 1.5 : 2,
            backgroundColor: 'background.default',
            borderRadius: 1,
            mb: 1,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }
        }}>
          {(Array.isArray(vehicle.documents) ? vehicle.documents : []).map((doc: VehicleDocument) => (
            <ListItem
              key={doc.url}
              secondaryAction={
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 1,
                  width: isMobile ? '100%' : 'auto',
                  mt: isMobile ? 1 : 0
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onImageClick(doc.url)}
                    fullWidth={isMobile}
                  >
                    {t('vehicle.details.buttons.view')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => onDeleteDocument(doc.url)}
                    fullWidth={isMobile}
                  >
                    {t('vehicle.details.buttons.delete')}
                  </Button>
                </Box>
              }
            >
              <ListItemText
                primary={doc.name}
                secondary={
                  <Box>
                    <Box>
                      {t('vehicle.details.fields.documentType')}: {doc.type}
                    </Box>
                    <Box>
                      {t('vehicle.details.fields.uploadedAt')}: {formatDate(doc.uploadedAt.toDate())}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
          {(!vehicle.documents || vehicle.documents.length === 0) && (
            <Typography color="textSecondary">{t('vehicle.details.messages.noDocuments')}</Typography>
          )}
        </List>
      </TabPanel>
    </Box>
  );
};

export default VehicleTabContent;
